import { useGetPreferenceTags } from 'api-hooks/preference-tag';
import { NavigationRoutes } from 'common/constants';
import { string2money } from 'common/utils/string';
import notification from 'components/common/notification';
import QueryFilter from 'components/common/query-filter/query-filter';
import ListHeader from 'components/elements/list-header';
import { useApplyQueryFilter } from 'hooks/filter/use-apply-query-filter';
import { FilterHandleParentProvider } from 'hooks/filter/use-filter-handle-parent';
import useApplyQuerySort from 'hooks/use-apply-query-sort';
import useComposedQuery from 'hooks/use-composed-query';
import useGetCrudPermission from 'hooks/use-get-crud-permission';
import useGetListParamsValue from 'hooks/use-get-list-params-value';
import {
  Container,
  SeparatorVertical,
  TopContainer,
} from 'legacy-modules/styles';
import useTranslation from 'next-translate/useTranslation';
import * as React from 'react';

import PreferenceTagTable from './components/table';

export default function PreferenceTagList() {
  const { t } = useTranslation();
  const { queryPage } = useGetListParamsValue();
  const [page, setPage] = React.useState(queryPage);

  const _columns = React.useMemo(
    () => [
      {
        header: t('common:name'),
        accessorKey: 'name',
        minSize: 250,
        sortName: 'name',
      },
      {
        header: t('common:type'),
        accessorKey: 'type',
        minSize: 250,
        sortName: 'type',
        cell: ({ getValue }) => <>{t(`common:${getValue() as string}`)}</>,
      },
      {
        header: t('common:total'),
        accessorKey: 'preferences',
        minSize: 250,
        cell: ({ getValue }) => <>{string2money(getValue().length)}</>,
      },
    ],
    [t],
  );

  const {
    data,
    refetch,
    isLoading,
    isFetching,
    error,
    query,
    extras: [{ filters, setFilters }, { columns }],
  } = useComposedQuery(
    useGetPreferenceTags,
    {
      params: {
        page,
      },
    },
    useApplyQueryFilter((data: any) => {
      return data.filters;
    }),
    useApplyQuerySort((data: any) => {
      return data.sorts;
    }, _columns),
  );

  React.useEffect(() => {
    if (!!error && !!error.message) {
      notification.error({ message: error?.message });
    }
  }, [error]);
  const permission = useGetCrudPermission('preference-groups');
  return (
    <FilterHandleParentProvider
      values={{
        filters,
        setFilters,
        page,
        setPage,
      }}
    >
      <Container>
        <ListHeader
          query={query}
          title={t('common:preference_tag')}
          showAdd={permission.create}
          onRefresh={refetch}
          linkAdd={NavigationRoutes.PreferenceTagsCreate}
        />
        <SeparatorVertical $margin={8} />
        <TopContainer>
          <QueryFilter filters={filters} isUseResponseOptions />
        </TopContainer>
        <PreferenceTagTable
          preferenceTags={data?.data || []}
          loading={isLoading || isFetching}
          page={page}
          onPageChange={(page) => setPage(page)}
          meta={data?.meta}
          sortColumns={columns || []}
        />
      </Container>
    </FilterHandleParentProvider>
  );
}
